import { inject, Injectable } from '@angular/core';
import { Display } from 'src/app/api/services/display';
import { DisplayApiService } from 'src/app/api/services/display-api.service';
import { FoldersApiService } from 'src/app/api/services/folders-api.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { MoveFolderDialogComponent } from 'src/app/components/folders/move-folder-dialog/move-folder-dialog.component';
import { RenameDialogComponent } from 'src/app/components/input-dialog/rename-dialog.component';
import { ModalService } from 'src/app/components/modals/modal.service';
import { CurrentPlanService } from 'src/app/components/plans/current-plan.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { ListOperations } from 'src/app/components/scrolling-list';
import { CompanyIdModalComponent } from 'src/app/components/scrolling-list/company-id-modal/company-id-modal.component';
import { SchedulePickerModalComponent } from 'src/app/schedules/components/schedule-picker-modal/schedule-picker-modal.component';
import { ScheduleService } from 'src/app/schedules/services/schedule.service';
import { EditAddressComponent } from '../components/edit-address/edit-address.component';
import { EditMonitoringComponent } from '../components/edit-monitoring/edit-monitoring.component';
import { EditRebootTimeComponent } from '../components/edit-reboot-time/edit-reboot-time.component';
import { EditScreenSharingComponent } from '../components/edit-screen-sharing/edit-screen-sharing.component';
import { DISPLAY_FOLDER_OPERATION_SERVICE } from '../providers/display-folder-operation.provider';
import { DisplayControlService } from './display-control.service';
import { DisplayService } from './display.service';
import { PlayerActionsService } from './player-actions.service';
import { PlayerLicenseService } from './player-license.service';

@Injectable({
  providedIn: 'root'
})
export class DisplayListOperationsService {

  listOperations = {
    name: 'Display',
    operations: [{
        name: 'Restart Rise Player',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmRestart(items),
            selected
          );
        },
        actionCall: (item) => {
          return this.folderOperationService.executeSingleOperation(
            this.playerActionsService.restartByObject.bind(this.playerActionsService),
            item.item
          );
        },
        requireRole: 'da',
        mainOperation: true
      },
      {
        name: 'Reboot Media Player',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmReboot(items),
            selected
          );
        },
        actionCall: (item) => {
          return this.folderOperationService.executeSingleOperation(
            this.playerActionsService.rebootByObject.bind(this.playerActionsService),
            item.item
          );
        },
        requireRole: 'da',
        mainOperation: true
      },
      {
        name: 'Update Firmware',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmUpdateFirmware(items),
            selected
          );
        },
        actionCall: (item, args) => {
          const firmwareUrl = args?.firmwareUrl;
          return this.folderOperationService.executeSingleOperation(
            (display) => this.playerActionsService.updateFirmwareByObject(display, firmwareUrl),
            item.item
          );
        },
        requireRole: 'sa',
        mainOperation: true
      },
      {
        name: 'License',
        actionCall: (selected) => {
          return this.folderOperationService.executeGroupOperation(
            (selection) => this._licenseDisplays(selection.companyId, selection.items),
            {...selected, items: selected.items.map(item => item.item)}
          );
        },
        beforeBatchAction: (selected) => this._confirmLicense(selected.map(item => item.item)),
        groupBy: 'companyId',
        filter: (listItem) => {
          return !(listItem.item as Display).playerProAuthorized;
        },
        deepFilter: true,
        requireRole: 'da',
        mainOperation: true
      },
      {
        name: 'Screen Sharing',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmScreenSharing(items),
            selected
          );
        },
        actionCall: (item, fields) => {
          return this.folderOperationService.executeSingleOperation(
            (display) => this.displayService.applyFields(display, fields),
            item.item
          );
        },
        requireRole: 'da',
      },
      {
        name: 'Assign Schedule',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmAssignSchedule(items),
            selected
          );
        },
        actionCall: (selected, schedule) => {
          return this.folderOperationService.executeGroupOperation(
            (selection) => this.scheduleService.addAllToDistribution(selection.items, schedule),
            {...selected, items: selected.items.map(item => item.item)}
          );
        },
        groupBy: true,
        requireRole: 'cp'
      },
      {
        separator: true
      },
      {
        name: 'Set Monitoring',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmSetMonitoring(items),
            selected
          );
        },
        actionCall: (item, fields) => {
          return this.folderOperationService.executeSingleOperation(
            (display) => this.displayService.applyFields(display, fields),
            item.item
          );
        },
        requireRole: 'da'
      },
      {
        name: 'Set Reboot Time',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmSetRebootTime(items),
            selected
          );
        },
        actionCall: (item, fields) => {
          return this.folderOperationService.executeSingleOperation(
            (display) => this.displayService.applyFields(display, fields),
            item.item
          );
        },
        requireRole: 'da'
      },
      {
        name: 'Set Address',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmSetAddress(items),
            selected
          );
        },
        actionCall: (item, fields) => {
          return this.folderOperationService.executeSingleOperation(
            (display) => this.displayService.applyFields(display, fields),
            item.item
          );
        },
        requireRole: 'da'
      },
      {
        separator: true
      },
      {
        name: 'Enable Display Control',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmEnableDisplayControl(items),
            selected
          );
        },
        actionCall: (item) => {
          return this.folderOperationService.executeSingleOperation(
            this.displayControlService.enableDisplayControl.bind(this.displayControlService),
            item.item
          );
        },
        requireRole: 'da'
      },
      {
        name: 'Disable Display Control',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmDisableDisplayControl(items),
            selected
          );
        },
        actionCall: (item) => {
          return this.folderOperationService.executeSingleOperation(
            this.displayControlService.disableDisplayControl.bind(this.displayControlService),
            item.item
          );
        },
        requireRole: 'da'
      },
      {
        separator: true
      },
      {
        name: 'Move To Company',
        beforeBatchAction: (selected) => {
          return this.folderOperationService.executeSingleBeforeBatchAction(
            (items) => this._confirmMove(items),
            selected
          );
        },
        actionCall: async (selected, companyId) => {
          if (selected.item.companyId === companyId) {
            return Promise.resolve();
          }
          return this.folderOperationService.executeSingleOperation(
            (item) => this.displayApiService.move(item.id, companyId),
            selected.item
          );
        },
        reloadList: true,
        requireRole: 'da'
      },
      {
        name: 'Export All',
        beforeBatchAction: (selected) => this._confirmExport(selected.map(item => item.item)),
        actionCall: this.displayApiService.export.bind(this.displayApiService),
        groupBy: true //group all
      },
      {
        name: "Rename",
        beforeBatchAction: (selected) => this.actionRename(selected.map(item => item.item)),
        showIn: ['single'],
        icon: 'pencil-line',
        reloadList: true
      },
      {
        name: 'Move',
        beforeBatchAction: (selected) => this.beforeActionMoveFolder(selected.map(item => item.item)),
        actionCall: (selected, folderId) => this.actionMoveFolder(selected.item, folderId),
        showIn: ['single', 'bulk'],
        icon: 'move-to-folder',
        reloadList: true
      },
      {
        name: 'Delete',
        actionCall: async (listItem) => {
          await this.folderOperationService.executeSingleOperation(
            this.displayService.deleteDisplayByObject.bind(this.displayService),
            listItem.item
          );
          if (listItem.isFolder) {
            await this.foldersApiService.delete(listItem.item.id);
          }
          return Promise.resolve();
        },
        requireRole: 'da',
        showIn: ['bulk', 'single'],
        icon: 'trash',
        isDelete: true,
      }
    ]
  } as ListOperations<Display>;

  private folderOperationService = inject(DISPLAY_FOLDER_OPERATION_SERVICE);

  constructor(
    private userStateService: UserStateService,
    private companyStateService: CompanyStateService,
    private playerActionsService: PlayerActionsService,
    private playerLicenseService: PlayerLicenseService,
    private currentPlanService: CurrentPlanService,
    private displayService: DisplayService,
    private displayApiService: DisplayApiService,
    private scheduleService: ScheduleService,
    private displayControlService: DisplayControlService,
    private featuresService: FeaturesService,
    private modalService: ModalService,
    private foldersApiService: FoldersApiService,
  ) { }

  private _confirmLicense (selected) {
    const company = this.companyStateService.getCopyOfSelectedCompany(true);
    if (!selected.length) {
      this.modalService.showMessage(
        'Already licensed!',
        'Please select some unlicensed displays to license.');
      return Promise.reject();
    } else if (this.playerLicenseService.getProAvailableLicenseCount() < selected.length) {
      this.currentPlanService.confirmAndPurchase(selected.length - this.playerLicenseService.getProAvailableLicenseCount());
      return Promise.reject();
    } else if (this._hasSubcompanyDisplays(selected) && !company.shareCompanyPlan) {
      this.modalService.showMessage(
        'Unable to share licenses!',
        'Some of the selected displays are for sub-companies, and your current plan does not allow sharing licenses. ' +
        'Please select displays from your company only.');
      return Promise.reject();
    } else {
      return this.modalService.confirm(
        'Assign license?',
        'Do you want to assign licenses to the selected displays?',
        'Yes',
        'No');
    }
  }

  private _licenseDisplays (companyId, displays) {
    const displayIds = displays.map(display => display.id);

    return this.playerLicenseService.licenseDisplaysByCompanyId(companyId, displayIds)
      .then(() => {
        displays.forEach((display) => {
          display.playerProAuthorized = true;
        });
      });
  }

  private _checkLicenses (selectedItems) {
    const notAuthorized = [];
    selectedItems.forEach((display) => {
      if (!display.playerProAuthorized) {
        notAuthorized.push(display.id);
      }
    });
    if (notAuthorized.length > 0) {
      const availableLicenses = this.playerLicenseService.getProAvailableLicenseCount();
      const availableLicensesText = this.currentPlanService.isUnlimitedPlan() ? 'unlimited' : availableLicenses.toString();

      const company = this.companyStateService.getCopyOfSelectedCompany(true);
      if (this._hasSubcompanyDisplays(selectedItems) && !company.shareCompanyPlan) {
        this.modalService.showMessage(
          'Unable to complete bulk action!',
          'This action cannot be performed on unlicensed displays, and your current plan does not allow sharing licenses. ' +
          'Please select displays from your company only.');
        return Promise.reject();
      }

      let baseMessage = notAuthorized.length > 1 ?
        notAuthorized.length +
        ' of your selected displays are not licensed and to perform this action they need to be.' :
        '1 of your selected displays is not licensed and to perform this action it needs to be.';
      baseMessage += ' You have ' + availableLicensesText + ' available license' + (availableLicenses !== 1 ?
        's' : '');

      if (availableLicenses >= notAuthorized.length) {
        return this.modalService.confirm('Almost there!',
          baseMessage + '. Do you want to assign ' + notAuthorized.length + (notAuthorized.length > 1 ?
            ' to these displays?' : ' to this display?'), 'Yes', 'No').then(() => {
            const licenseOperation = this.listOperations.operations.find((o) => {
              return o.name === 'License';
            });
            return (licenseOperation as any).onClick(true);
          });
      } else {
        return this.modalService.confirm('Almost there!',
          baseMessage + ' and you need to subscribe for ' + (notAuthorized.length - availableLicenses) +
          ' more to license ' + (notAuthorized.length > 1 ? 'these displays.' : 'this display.'),
          'Subscribe', 'Cancel').then(() => {
          this.currentPlanService.showPurchaseOptions(notAuthorized.length - availableLicenses);
          return Promise.reject();
        });
      }
    }
    return Promise.resolve();
  }

  private _confirmAssignSchedule (selectedItems) {
    if (this._hasSubcompanyDisplays(selectedItems)) {
      this.modalService.showMessage(
        'Schedule could not be assigned!',
        'Your schedule cannot be assigned to displays that belong to your sub-companies. <br/>Please select displays from your company only.');
      return Promise.reject();
    }
    return this.modalService.showDialog(SchedulePickerModalComponent, null, {
      width: this.modalService.MODAL_SM
    });
  }

  private _hasSubcompanyDisplays (selectedItems) {
    const cid = this.companyStateService.getSelectedCompanyId();
    return selectedItems.find((d) => {
      return d.companyId !== cid;
    });
  }

  private _confirmPlayerAction (selectedItems, isRestart) {
    const suffix = selectedItems.length > 1 ? 's' : '';
    const title = isRestart ? 'Restart Rise Player' + suffix + '?' : 'Reboot media player' + suffix + '?';
    let message = isRestart ? 'Rise Player' + suffix + ' will restart' : 'The media player' + suffix +
      ' will reboot';
    message += ' and the content showing on your display' + suffix +
      ' will be interrupted. Do you wish to proceed?';

    return this._checkLicenses(selectedItems).then(() => {
      return this.modalService.confirm(title, message, 'Yes', 'No');
    });
  }

  private _confirmFirmwareAction (selectedItems) {
    return this._checkLicenses(selectedItems).then(() => {
      return this.modalService.showFirmwareUpdateModal({
        displayName: selectedItems.length === 1 ? selectedItems[0].name : undefined,
        isMultiple: selectedItems.length > 1
      }).then((result) => {
        if (result && result.firmwareUrl) {
          return { firmwareUrl: result.firmwareUrl };
        }
        throw new Error('Firmware update cancelled');
      });
    });
  }

  private _confirmRestart (selectedItems) {
    return this._confirmPlayerAction(selectedItems, true);
  }

  private _confirmReboot (selectedItems) {
    return this._confirmPlayerAction(selectedItems, false);
  }

  private _confirmUpdateFirmware (selectedItems) {
    return this._confirmFirmwareAction(selectedItems);
  }

  private _confirmScreenSharing(selectedItems) {
    return this._checkLicenses(selectedItems).then(() => {
      return this.modalService.showBulkEditModal(
        'Set Screen Sharing',
        {
          screenSharingEnabled: true,
          screenSharingStandard: true,
          settings: {
            screenSharingMode: 'standard',
            screenSharingPinRequired: false,
            screenSharingAuthenticationRequired: false,
            screenSharingCodeHidden: false
          }
        },
        EditScreenSharingComponent
      );
    });
  }

  private _confirmSetMonitoring (selectedItems) {
    if (this.featuresService.isFeatureAvailable('monitoring')) {
      return this._checkLicenses(selectedItems).then(() => {
        return this.modalService.showBulkEditModal(
          'Set Monitoring',
          {
            monitoringEnabled: true,
            monitoringEmails: null,
            monitoringSchedule: null
          },
          EditMonitoringComponent
        );
      });
    }
    this.featuresService.showUpgradePlanModal('monitoring');
    return Promise.reject();
  }

  private _confirmSetRebootTime (selectedItems) {
    return this._checkLicenses(selectedItems).then(() => {
      return this.modalService.showBulkEditModal(
        'Set Reboot Time',
        {
          restartEnabled: true,
          restartTime: '02:00',
        },
        EditRebootTimeComponent
      );
    });
  }

  private _confirmSetAddress (selectedItems) {
    return this._checkLicenses(selectedItems).then(() => {
      return this.modalService.showBulkEditModal(
        'Set Address',
        {
          useCompanyAddress: false,
          addressDescription: '',
          street: '',
          unit: '',
          city: '',
          country: '',
          province: '',
          postalCode: '',
          timeZoneOffset: null
        },
        EditAddressComponent
      );
    });
  }

  private _confirmEnableDisplayControl (selectedItems) {
    return this._checkLicenses(selectedItems).then(() => {
      if (this.featuresService.isFeatureAvailable('display_control')) {
        return this.modalService.confirm(
          'Enable Display Control?',
          'Do you want to enable display control for the selected displays?',
          'Yes',
          'No');
      }
      this.featuresService.showUpgradePlanModal('display_control');
      return Promise.reject();
    });
  }

  private _confirmDisableDisplayControl (selectedItems) {
    return this._checkLicenses(selectedItems).then(() => {
      if (this.featuresService.isFeatureAvailable('display_control')) {
        return this.modalService.confirm(
          'Disable Display Control?',
          'Do you want to disable display control for the selected displays?',
          'Yes',
          'No');
      }
      this.featuresService.showUpgradePlanModal('display_control');
      return Promise.reject();
    });
  }

  private _confirmExport (selectedItems) {
    if (!this.currentPlanService.isPlanActive() && !this.userStateService.isRiseAdmin()) {
      this.currentPlanService.showUnlockThisFeatureModal();
      return Promise.reject();
    }
    return this.modalService.confirm('Export displays?',
      'An export file will be prepared and emailed to you at <b>' + this.userStateService.getUserEmail() +
      '</b> once ready.<br/> Please ensure your email is configured to accept emails from <b>no-reply@risevision.com</b>.',
      'Export', 'Cancel');
  }

  private _confirmMove (selectedItems) {
    const plural = selectedItems.length > 1 ? 's' : '';
    return this.modalService.showDialog(CompanyIdModalComponent, {
      title: 'Move Selected Display' + plural,
      description: 'The selected display' + plural + ' will be moved to the company ID specified below.'
    });
  }

  private async beforeActionMoveFolder(selectedItems: any) {
    const folders = selectedItems.filter(item => item.isFolder);
    return this.modalService.showDialog(MoveFolderDialogComponent, {
      kind: 'Display',
      parentId: selectedItems[0].parentId,
      folderId: selectedItems[0].id,
      folders: folders.map(item => item.id)
    }, {width: this.modalService.MODAL_MD}).then(async (parentId: string) => {
      if (parentId === undefined || parentId === null) return Promise.reject();
      return Promise.resolve(parentId);
    });
  }

  private async actionMoveFolder(selectedItem: any, parentId: string) {
    if (selectedItem.kind) {
      await this.foldersApiService.update(selectedItem.id, {
        ...selectedItem,
        parentId
      });
    } else {
      await this.displayApiService.update(selectedItem.id, {
        folderId: parentId
      });
    }
  }

  private actionRename(selectedItems: any) {
    const [item] = selectedItems;
    return this.modalService.showDialog(RenameDialogComponent, {
      title: 'Rename',
      placeholder: 'Enter display name',
      value: selectedItems[0].name,
      notAllowedNames: []
    }).then(async (name: string | null) => {
      if (!name) return Promise.reject();
      if (item.isFolder) {
        try {
          await this.foldersApiService.update(selectedItems[0].id, {
            ...item,
            name,
            parentId: item.parentId ? item.parentId : undefined
          });
        } catch (err) {
          this.modalService.showMessage('Error', err.message);
          return Promise.reject();
        }
      } else {
        await this.displayApiService.update(selectedItems[0].id, {
          name
        });
      }
    });
  }
}
